import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import {
  CheckCircle,
  XCircle,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import affiliateService from "../../requests/affiliates/AffiliateService";
import ApproveApplicationModal from "./ApproveApplicationModal";
import RejectApplicationModal from "./RejectApplicationModal";

const columnHelper = createColumnHelper();

const AffiliateApplicationsTable = ({ onStatsUpdate }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const handleViewDetails = useCallback((application) => {
    // Check if we have affiliate data or at least an affiliate_code
    if (!application?.affiliate?.affiliate_code && !application?.affiliate_code) {
      toast.error("Unable to view details - missing affiliate information");
      return;
    }
    
    // Use affiliate_code from affiliate object if available, otherwise from root level
    const affiliateCode = application.affiliate?.affiliate_code || application.affiliate_code;
    
    navigate(`/affiliate-details/${affiliateCode}`, {
      state: { application }
    });
  }, [navigate]);


  const handleViewDealDetails = useCallback((deal, application) => {
    // Navigate to deal details page with deal and application data
    if (!deal?.deal_slug) {
      toast.error("Unable to load deal details - missing deal information");
      return;
    }
    
    navigate(`/deal-details/${deal.deal_slug}`, {
      state: { 
        deal,
        application
      }
    });
  }, [navigate]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("affiliate_code", {
        header: "Affiliate Code",
        cell: (info) => (
          <div className="inline-flex items-center px-3 py-1 text-sm font-semibold text-blue-800 bg-blue-100 rounded-full">
            {info.getValue() || 'N/A'}
          </div>
        ),
      }),
      columnHelper.accessor("affiliate.first_name", {
        header: "Affiliate Name",
        cell: (info) => {
          const firstName = info.getValue();
          const lastName = info.row.original.affiliate?.last_name;
          const affiliateCode = info.row.original.affiliate_code;
          
          // If affiliate is null, show affiliate code as fallback
          if (!firstName && !lastName) {
            return (
              <div className="font-medium text-gray-900">
                {affiliateCode || 'N/A'}
              </div>
            );
          }
          
          return (
            <div className="font-medium text-gray-900">
              {firstName || 'N/A'} {lastName || ''}
            </div>
          );
        },
      }),
      columnHelper.accessor("affiliate.email", {
        header: "Email",
        cell: (info) => {
          const email = info.getValue();
          // If affiliate is null, show N/A
          if (!email) {
            return <div className="text-sm text-gray-500">N/A</div>;
          }
          return <div className="text-sm text-gray-600">{email}</div>;
        },
      }),
      columnHelper.accessor("deal.title", {
        header: "Deal Title",
        cell: (info) => {
          const deal = info.row.original.deal;
          const application = info.row.original;
          const dealTitle = info.getValue();
          
          if (!dealTitle) {
            return <div className="text-sm text-gray-500">N/A</div>;
          }
          
          return (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleViewDealDetails(deal, application);
              }}
              className="block max-w-xs font-medium text-left text-blue-600 truncate hover:text-blue-800 hover:underline"
              title={`Click to view deal details: ${dealTitle}`}
            >
              {dealTitle}
            </button>
          );
        },
      }),
      columnHelper.accessor("deal.brand_name", {
        header: "Brand",
        cell: (info) => (
          <div className="font-medium text-gray-900">{info.getValue() || 'N/A'}</div>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const status = info.getValue();
          const statusConfig = {
            pending: {
              bg: "bg-yellow-100",
              text: "text-yellow-800",
              icon: "⏳",
            },
            approved: {
              bg: "bg-green-100",
              text: "text-green-800",
              icon: "✅",
            },
            rejected: {
              bg: "bg-red-100",
              text: "text-red-800",
              icon: "❌",
            },
          };
          const config = statusConfig[status] || statusConfig.pending;
          
          return (
            <motion.span 
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              <motion.span 
                className="mr-1"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {config.icon}
              </motion.span>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </motion.span>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Applied Date",
        cell: (info) => (
          <div className="text-sm text-gray-600">
            {format(new Date(info.getValue()), "MMM dd, yyyy")}
          </div>
        ),
      }),
      columnHelper.accessor("id", {
        header: "Actions",
        cell: (info) => {
          const application = info.row.original;
          const isPending = application.status === "pending";
          
          return (
            <div className="flex items-center space-x-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleViewDetails(application);
                }}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </button>
              
              {isPending && (
                <>
                  <motion.button
                    onClick={() => handleApprove(application)}
                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Approve"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    onClick={() => handleReject(application)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Reject"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                  >
                    <XCircle className="w-4 h-4" />
                  </motion.button>
                </>
              )}
            </div>
          );
        },
      }),
    ],
    [handleViewDetails, handleViewDealDetails]
  );

  const filteredData = useMemo(() => {
    if (statusFilter === "all") return data;
    return data.filter(item => item.status === statusFilter);
  }, [data, statusFilter]);

  // Use ref to store the latest onStatsUpdate function
  const onStatsUpdateRef = useRef(onStatsUpdate);
  onStatsUpdateRef.current = onStatsUpdate;

  // Calculate stats from data
  const calculateStats = useCallback((applications) => {
    const stats = {
      total: applications.length,
      pending: applications.filter(app => app.status === 'pending').length,
      approved: applications.filter(app => app.status === 'approved').length,
      rejected: applications.filter(app => app.status === 'rejected').length
    };
    
    return stats;
  }, []);

  // Update stats whenever data changes
  useEffect(() => {
    const stats = calculateStats(data);
    if (onStatsUpdateRef.current) {
      onStatsUpdateRef.current(stats);
    }
  }, [data, calculateStats]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await affiliateService.getAffiliateApplications();
      if (response.status) {
        setData(response.data.data || []);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to fetch applications");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchApplications();
  }, []);

  const handleApprove = (application) => {
    setSelectedApplication(application);
    setShowApproveModal(true);
  };

  const handleReject = (application) => {
    setSelectedApplication(application);
    setShowRejectModal(true);
  };

  const handleApproveSuccess = () => {
    setShowApproveModal(false);
    setSelectedApplication(null);
    fetchApplications();
    toast.success("Application approved successfully!");
  };

  const handleRejectSuccess = () => {
    setShowRejectModal(false);
    setSelectedApplication(null);
    fetchApplications();
    toast.success("Application rejected successfully!");
  };

  const handleRefresh = () => {
    fetchApplications();
  };

  return (
    <motion.div 
      className="bg-white rounded-xl border border-gray-200 shadow-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-200 lg:px-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 lg:text-xl">
              Affiliate Applications
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage affiliate applications for deal promotions
            </p>
          </div>
          <div className="flex items-center space-x-2 lg:space-x-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-4 border-b border-gray-200 lg:px-6">
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search applications..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="py-2 pr-4 pl-10 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:w-64"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="text-sm text-center text-gray-600 sm:text-right">
            {table.getFilteredRowModel().rows.length} applications
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-3 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase whitespace-nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <motion.div 
                    className="flex justify-center items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <RefreshCw className="mr-2 w-6 h-6 text-blue-600" />
                    </motion.div>
                    <span className="text-gray-600">Loading applications...</span>
                  </motion.div>
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <motion.div 
                    className="text-gray-500"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <motion.div 
                      className="mb-4 text-4xl"
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{ 
                        duration: 2, 
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      📋
                    </motion.div>
                    <div className="text-lg font-medium">No applications found</div>
                    <div className="text-sm">No affiliate applications match your current filters.</div>
                  </motion.div>
                </td>
              </tr>
            ) : (
              <AnimatePresence>
                {table.getRowModel().rows.map((row, index) => (
                  <motion.tr
                    key={row.id}
                    className="transition-colors hover:bg-gray-50"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ 
                      duration: 0.3, 
                      delay: index * 0.05,
                      ease: "easeOut" 
                    }}
                    whileHover={{ 
                      scale: 1.01,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-4 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </motion.tr>
                ))}
              </AnimatePresence>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {table.getPageCount() > 1 && (
        <div className="px-4 py-4 border-t border-gray-200 lg:px-6">
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="text-sm text-center text-gray-700 sm:text-left">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{" "}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{" "}
              of {table.getFilteredRowModel().rows.length} results
            </div>
            <div className="flex justify-center items-center space-x-2">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showApproveModal && selectedApplication && (
          <ApproveApplicationModal
            application={selectedApplication}
            onClose={() => {
              setShowApproveModal(false);
              setSelectedApplication(null);
            }}
            onSuccess={handleApproveSuccess}
          />
        )}

        {showRejectModal && selectedApplication && (
          <RejectApplicationModal
            application={selectedApplication}
            onClose={() => {
              setShowRejectModal(false);
              setSelectedApplication(null);
            }}
            onSuccess={handleRejectSuccess}
          />
        )}
      </AnimatePresence>

    </motion.div>
  );
};

export default AffiliateApplicationsTable;
